import { MediaType } from "service/project/api/media";
import { WorkTypeCategory } from "types/project/projectDefaults";

export const formatDate = (
  date: string | Date | number | null | undefined,
  fallback: string = ""
): string => {
  if (!date) return fallback;

  let dateToFormat: Date;

  if (typeof date === "string") {
    // Check if it's a date key (YYYY-MM-DD) to prevent UTC shift
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      dateToFormat = parseDateKey(date);
    } else {
      dateToFormat = new Date(date);
    }
  } else if (typeof date === "number") {
    dateToFormat = new Date(date);
  } else if (date instanceof Date) {
    dateToFormat = date;
  } else {
    return fallback;
  }

  return dateToFormat.toLocaleDateString("default", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

// Date + time, for a specific-moment timestamp (e.g. "effective at") where
// showing only the date is ambiguous about whether that moment is still
// upcoming or has already passed today. Converts to the viewer's local time.
export const formatDateTime = (
  date: string | Date | number | null | undefined,
  fallback: string = ""
): string => {
  if (!date) return fallback;
  const dateToFormat = date instanceof Date ? date : new Date(date);
  if (isNaN(dateToFormat.getTime())) return fallback;

  return dateToFormat.toLocaleString("default", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

// Like formatDateTime, but for a moment that's only meaningful in a specific
// timezone (e.g. a scheduled Attendance Policy change effective in that
// policy's own timezone) — rendering it in the viewer's device timezone
// instead would show a different local date/time to every viewer for the
// same actual moment. Always pass the timezone the backend attaches to the
// field (e.g. scheduledAttendancePolicyTimezone), never assume one.
export const formatDateTimeInTimezone = (
  date: string | Date | number | null | undefined,
  timeZone?: string | null,
  fallback: string = ""
): string => {
  if (!date) return fallback;
  const dateToFormat = date instanceof Date ? date : new Date(date);
  if (isNaN(dateToFormat.getTime())) return fallback;

  const options: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    ...(timeZone ? { timeZone } : {}),
  };

  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat("en-US", options).formatToParts(dateToFormat);
  } catch {
    // Unrecognized/invalid IANA timezone string — fall back to the device
    // timezone rather than throwing, so a bad value never blanks the field.
    parts = new Intl.DateTimeFormat("en-US", { ...options, timeZone: undefined }).formatToParts(dateToFormat);
  }
  const get = (type: string) => parts.find((p) => p.type === type)?.value || "";
  // "8 Oct 2026, 9:00 AM"
  const formatted = `${get("day")} ${get("month")} ${get("year")}, ${get("hour")}:${get("minute")} ${get("dayPeriod")}`;

  return timeZone ? `${formatted} (${timeZone})` : formatted;
};

// "8 Oct 2026" whatever the viewer's locale — formatDate follows the
// browser's, which reads the same date as "Oct 8, 2026" on an en-US one.
export const formatDayMonthYear = (
  date: string | Date | null | undefined,
  fallback: string = ""
): string => {
  if (!date) return fallback;
  const dateToFormat =
    typeof date === "string" && /^\d{4}-\d{2}-\d{2}/.test(date)
      ? parseDateKey(date.slice(0, 10))
      : new Date(date);
  if (isNaN(dateToFormat.getTime())) return fallback;
  return dateToFormat.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

export const getDaysInMonth = (date: Date): Date[] => {
  const year: number = date.getFullYear();
  const month: number = date.getMonth();
  const firstDay: Date = new Date(year, month, 1);
  const startDate: Date = new Date(firstDay);
  // Monday-first grid, matching dayNames — getDay() is 0=Sun..6=Sat, so
  // shift it to a 0=Mon..6=Sun offset before subtracting.
  startDate.setDate(startDate.getDate() - ((firstDay.getDay() + 6) % 7));

  const days: Date[] = [];
  const currentDate: Date = new Date(startDate);

  for (let i = 0; i < 42; i++) {
    days.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return days;
};

export const getCurrentWeekDays = (date: Date): Date[] => {
  const startOfWeek = new Date(date);
  const dayOfWeek = startOfWeek.getDay();
  // Monday-first, matching getDaysInMonth above.
  startOfWeek.setDate(startOfWeek.getDate() - ((dayOfWeek + 6) % 7));

  const days: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const day = new Date(startOfWeek);
    day.setDate(startOfWeek.getDate() + i);
    days.push(day);
  }
  return days;
};

export const formatDateKey = (date: Date): string => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(date.getDate()).padStart(2, "0")}`;
};

export const parseDateKey = (dateStr: string): Date => {
  if (!dateStr) return new Date();
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day);
};

export const getMediaType = (file: File): MediaType => {
  if (file.type.startsWith("image/")) return "IMAGE";
  if (file.type.startsWith("video/")) return "VIDEO";
  if (file.type === "application/pdf" || file.name.endsWith(".pdf"))
    return "DOCUMENT_PDF";
  if (
    file.type === "application/msword" ||
    file.type ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    file.name.endsWith(".doc") ||
    file.name.endsWith(".docx")
  ) {
    return "DOCUMENT_WORD";
  }
  return "DOCUMENT_OTHER";
};

export const HIERARCHY_RULES: Record<WorkTypeCategory, WorkTypeCategory[]> = {
  [WorkTypeCategory.EPIC]: [
    WorkTypeCategory.STORY,
    WorkTypeCategory.TASK,
    WorkTypeCategory.BUG,
  ],
  [WorkTypeCategory.STORY]: [WorkTypeCategory.TASK, WorkTypeCategory.BUG],
  [WorkTypeCategory.TASK]: [WorkTypeCategory.SUB_TASK],
  [WorkTypeCategory.BUG]: [WorkTypeCategory.SUB_TASK],
  [WorkTypeCategory.SUB_TASK]: [],
};

export const getHierarchyWarning = (
  newParentCategory: WorkTypeCategory,
  childCategories: WorkTypeCategory[]
): string | null => {
  if (childCategories.length === 0) return null;

  const allowedChildren = HIERARCHY_RULES[newParentCategory] || [];
  const invalidChildren = childCategories.filter(
    (cat) => !allowedChildren.includes(cat)
  );

  if (invalidChildren.length > 0) {
    switch (newParentCategory) {
      case WorkTypeCategory.TASK:
      case WorkTypeCategory.BUG:
        return `This work item has child items. Tasks or Bugs can only have Sub-Tasks as child items.`;
      case WorkTypeCategory.STORY:
        return `This work item has child items. Stories can only have Task or Bug as child items.`;
      case WorkTypeCategory.SUB_TASK:
        return `Sub-tasks cannot have child items. Changing to Sub-task will violate the hierarchy.`;
      default:
        return `This work item has child items that may not be compatible with the new type.`;
    }
  }

  return null;
};

// ---------------------------------------------------------------------------
// Comment thread helpers
// ---------------------------------------------------------------------------

/** Colours cycle by nesting depth for the L-shaped connector lines. Soft neutral shades for dark theme. */
export const COMMENT_CONNECTOR_COLORS = ['#4b5563', '#525b6a', '#3f4451', '#374151', '#2f3646'];

export const getConnectorColor = (depth: number): string =>
  COMMENT_CONNECTOR_COLORS[depth % COMMENT_CONNECTOR_COLORS.length];

/** "December 18, 2025 at 8:23 PM" — used in comment timestamps. */
export const getExactFormatDate = (dateString: string): string => {
  const d = new Date(dateString);
  const datePart = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const timePart = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${datePart} at ${timePart}`;
};

// INVERSE of HIERARCHY_RULES
export const VALID_PARENT_CATEGORIES: Partial<Record<WorkTypeCategory, WorkTypeCategory[]>> = {
  [WorkTypeCategory.STORY]:   [WorkTypeCategory.EPIC],
  [WorkTypeCategory.TASK]:    [WorkTypeCategory.EPIC, WorkTypeCategory.STORY],
  [WorkTypeCategory.BUG]:     [WorkTypeCategory.EPIC, WorkTypeCategory.STORY],
  [WorkTypeCategory.SUB_TASK]: [WorkTypeCategory.TASK, WorkTypeCategory.BUG],
};
export const getErrorMessage = (error: any, fallback: string = 'An error occurred'): string => {
  if (error?.response?.data?.errors && Array.isArray(error.response.data.errors) && error.response.data.errors.length > 0) {
    return error.response.data.errors[0].message || fallback;
  }
  return error?.response?.data?.message || error?.message || fallback;
};

export const safeParseDate = (dateVal: string | Date | null | undefined): Date | null => {
  if (!dateVal) return null;
  if (dateVal instanceof Date) {
    return isNaN(dateVal.getTime()) ? null : dateVal;
  }
  
  // Check if it's a date key (YYYY-MM-DD) to prevent UTC shift
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateVal)) {
    return parseDateKey(dateVal);
  }
  
  const parsed = new Date(dateVal);
  return isNaN(parsed.getTime()) ? null : parsed;
};

export const convertToDateKey = (dateVal: string | Date | null | undefined): string => {
  const date = safeParseDate(dateVal);
  if (!date) return '';
  return formatDateKey(date);
};

export const getProjectIconPath = (iconId?: string): string => {
  if (!iconId) return '';
  const PROJECT_ICON_MAP: Record<string, string> = {
    pIcon1: '/assets/pIcon1.png', pIcon2: '/assets/pIcon2.png',
    pIcon3: '/assets/pIcon3.png', pIcon4: '/assets/pIcon4.png',
    pIcon5: '/assets/pIcon5.png', pIcon6: '/assets/pIcon6.png',
  };
  return PROJECT_ICON_MAP[iconId] ?? '';
};


// Indian digit grouping (1,00,00,000) belongs to rupees only; every other
// currency is grouped in thousands (10,000,000).
export const formatDealAmount = (amount: number, currency: string) =>
  new Intl.NumberFormat(currency === 'INR' ? 'en-IN' : 'en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount);

// Contact/Company `dealValue` and kanban `totalAmount` are USD totals computed by the
// backend (each Deal converted, then summed) — always render them as USD.
export const formatUsdAmount = (amount: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount);

// A single Deal's value for display: the backend's USD conversion (`usdAmount`) when
// present, else the Deal's own amount/currency — never a `$` on an unconverted amount.
export const formatDealUsdAmount = (
  deal: { amount?: number | null; currency?: string | null; usdAmount?: number | null },
  fallback = '—',
) => {
  if (deal.usdAmount != null) return formatUsdAmount(deal.usdAmount);
  if (deal.amount != null) return formatDealAmount(deal.amount, deal.currency || 'INR');
  return fallback;
};


export const formatCloseLabel = (closeDate?: string) =>
  closeDate
    ? new Date(closeDate).toLocaleDateString('en-IN', {
        month: 'short',
        year: 'numeric',
      })
    : '—';

export const getUrlHost = (url?: string | null): string => {
  const raw = url?.trim();
  if (!raw) return '';

  const stripWww = (host: string) => host.replace(/^www\./i, '');

  try {
    const withProtocol = /^[a-z][a-z0-9+.-]*:\/\//i.test(raw) ? raw : `https://${raw}`;
    return stripWww(new URL(withProtocol).hostname);
  } catch {
    return stripWww(raw.replace(/^[a-z][a-z0-9+.-]*:\/\//i, '')).split(/[/?#]/)[0];
  }
}
export interface CheckInStatusDetails {
  label: string;
  badgeClass: string;
}

export const getCheckInStatusDetails = (status?: string | null): CheckInStatusDetails | null => {
  if (!status) return null;
  const normalized = status.toUpperCase().replace(/_/g, ' ');
  if (normalized === 'ON TIME' || normalized === 'ONTIME') {
    return {
      label: 'On Time',
      badgeClass: 'bg-[#10B981]/20 text-[#10B981]',
    };
  }
  if (normalized === 'LATE' || normalized === 'LATE CHECKIN') {
    return {
      label: 'Late',
      badgeClass: 'bg-[#F97316]/20 text-[#F97316]',
    };
  }
  const label = normalized
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
  return {
    label,
    badgeClass: 'bg-blue-500/20 text-blue-400',
  };
}
// Sentence case — capitalizes only the first letter of the whole string,
// leaving the rest untouched. Distinct from toTitleCase (which capitalizes
// every word): a free-text description like "have a 4+ years of experience
// in IT industry" should read as a sentence ("Have a 4+ years..."), not
// have every word capitalized.
export function capitalizeFirstLetter(str: string | undefined | null): string {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// Title-cases each word, but preserves a word already entered fully
// uppercase as an acronym (QA, HR, UI, UX) rather than lower-casing it —
// unlike toTitleCase, a long all-caps word (e.g. "ENGINEER") is NOT treated
// as an acronym and still gets title-cased, since someone typing a whole
// word in caps almost never means an abbreviation the way a short one does.
export function formatNamePreservingAcronyms(
  str: string | undefined | null,
): string {
  if (!str) return '';
  return str
    .trim()
    .split(/\s+/)
    .map((word) => {
      const isAcronym = word.length <= 4 && /^[A-Z]+$/.test(word);
      return isAcronym
        ? word
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

export function toTitleCase(str: string | undefined | null): string {
  if (!str) return '';
  return str
    .split(' ')
    // A word that's already all-uppercase in the source (e.g. "HR", "MIT")
    // is an abbreviation, not a word someone forgot to capitalize — lower-
    // casing it first would destroy that instead of title-casing it.
    .map((word) =>
      /^[A-Z]{2,}$/.test(word)
        ? word
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    )
    .join(' ');
}

// A blank line in the Tiptap editor (pressing Enter twice) is a literally
// empty <p></p> once serialized via editor.getHTML() — ProseMirror only
// renders a visual <br> inside it live, in the contenteditable DOM, never in
// the saved HTML. The CSS that gives that empty <p> height (`.prose
// p:empty`) only matches while it stays exactly childless end to end, so a
// single stray whitespace character — e.g. from the backend reformatting
// the stored HTML — silently breaks the match and collapses the blank line.
// Rewriting truly-empty paragraphs to hold a real <br> makes the blank line
// self-contained: it reserves its own line height in any renderer, with no
// CSS involved and nothing left to break downstream. Apply this both right
// before saving (RichTextEditor) and right before rendering saved HTML
// (read-only description views), so it's robust in both directions.
export function normalizeEmptyParagraphs(html: string): string {
  if (!html) return html;
  return html.replace(/<p(\s[^>]*)?>\s*<\/p>/g, '<p$1><br></p>');
}
